import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, FileText, Palette, ShoppingBag, Users, BarChart3, 
  ChevronDown, Eye, Truck, CheckCircle, XCircle, Clock, Search,
  Plus, Trash2, Edit, DollarSign, Hash, Building2, Upload, Save,
  TrendingUp, Image, Briefcase, List, Download, Send, MessageSquare, Settings, Tag, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { ContentState } from '../components/ContentState';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const CHART_COLORS = ['#6BCDB0', '#2D3E60', '#E85A72', '#F59E0B', '#8B5CF6', '#06B6D4'];

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  submitted: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  quoted: 'bg-indigo-100 text-indigo-800',
};

const orderStatuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const quoteStatuses = ['pending', 'quoted', 'accepted', 'rejected'];
const projectStatuses = ['submitted', 'in_progress', 'quoted', 'completed', 'cancelled'];

// ============ STATS CARDS ============
function StatsCards({ stats }) {
  const cards = [
    { label: 'Total Orders', value: stats.total_orders, icon: Package, color: 'text-blue-600 bg-blue-50' },
    { label: 'Pending Orders', value: stats.pending_orders, icon: Clock, color: 'text-yellow-600 bg-yellow-50' },
    { label: 'Revenue', value: `$${(stats.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'text-green-600 bg-green-50' },
    { label: 'Products', value: stats.total_products, icon: ShoppingBag, color: 'text-purple-600 bg-purple-50' },
    { label: 'Quotes', value: stats.total_quotes, icon: FileText, color: 'text-indigo-600 bg-indigo-50' },
    { label: 'Users', value: stats.total_users, icon: Users, color: 'text-coral bg-red-50' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      {cards.map((card, i) => (
        <motion.div
          key={card.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
          className="bg-white rounded-2xl p-4 border border-gray-200"
        >
          <div className={`w-10 h-10 rounded-xl ${card.color} flex items-center justify-center mb-3`}>
            <card.icon className="h-5 w-5" />
          </div>
          <p className="text-2xl font-heading font-bold text-navy">{card.value}</p>
          <p className="text-xs text-gray-500">{card.label}</p>
        </motion.div>
      ))}
    </div>
  );
}

const trackingStages = [
  { key: 'order_placed', label: 'Order Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'processing', label: 'In Production' },
  { key: 'picked_up', label: 'Picked Up' },
  { key: 'in_transit', label: 'In Transit' },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'delivered', label: 'Delivered' },
];

// ============ ORDERS TAB ============
function OrdersTab({ api }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', tracking_number: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [trackingNote, setTrackingNote] = useState('');
  const [approvedPartners, setApprovedPartners] = useState([]);
  const [assignPartner, setAssignPartner] = useState('');
  const [assignJobTitle, setAssignJobTitle] = useState('');
  const [assigning, setAssigning] = useState(false);

  const fetchOrders = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const { data } = await api.get(`/api/admin/orders${params}`);
      setOrders(data);
    } catch {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [filterStatus]);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const { data } = await api.get('/api/admin/approved-partners');
        setApprovedPartners(data);
      } catch { /* no partners yet */ }
    };
    loadPartners();
  }, [api]);

  const handleUpdateOrder = async () => {
    try {
      const body = {};
      if (updateData.status) body.status = updateData.status;
      if (updateData.tracking_number) body.tracking_number = updateData.tracking_number;
      await api.put(`/api/admin/orders/${selectedOrder.id}`, body);
      toast.success('Order updated! Status email sent to customer.');
      setSelectedOrder(null);
      setUpdateData({ status: '', tracking_number: '' });
      fetchOrders();
    } catch {
      toast.error('Failed to update order');
    }
  };

  const handleAssignToPartner = async () => {
    if (!assignPartner || !assignJobTitle) {
      toast.error('Select a partner and enter a job title');
      return;
    }
    setAssigning(true);
    try {
      const itemNames = selectedOrder.items?.map(i => i.product?.name || 'Item').join(', ') || '';
      await api.post('/api/admin/partner-jobs', {
        partner_id: assignPartner,
        order_id: selectedOrder.id,
        title: assignJobTitle,
        description: `Order #${selectedOrder.id.slice(0,8)} — ${itemNames}`,
      });
      toast.success('Job assigned to partner! Notification email sent.');
      setAssignPartner('');
      setAssignJobTitle('');
    } catch {
      toast.error('Failed to assign job');
    } finally {
      setAssigning(false);
    }
  };

  const filtered = orders.filter(o => 
    !searchQuery || o.id.includes(searchQuery) || 
    o.shipping_address?.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by order ID or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="admin-orders-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="admin-orders-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {orderStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <ContentState loading={loading} empty={filtered.length === 0} emptyContent={<div className="text-center py-12 text-gray-500">No orders found</div>}>
        <div className="space-y-3">
          {filtered.map(order => (
            <div key={order.id} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-mint/30 transition-all">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-sm font-medium text-navy">#{order.id.slice(0, 8)}</span>
                    <Badge className={statusColors[order.status]}>{order.status}</Badge>
                    <Badge variant="outline" className="capitalize">{order.payment_method}</Badge>
                    {order.payment_status === 'paid' && <Badge className="bg-green-100 text-green-800">Paid</Badge>}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {new Date(order.created_at).toLocaleDateString()} - {order.items?.length || 0} item(s) - {order.shipping_address?.city || 'N/A'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-navy">${order.total?.toFixed(2)}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSelectedOrder(order); setUpdateData({ status: order.status, tracking_number: order.tracking_number || '' }); }}
                    data-testid={`admin-order-edit-${order.id.slice(0,8)}`}
                  >
                    <Edit className="h-4 w-4 mr-1" /> Manage
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ContentState>

      {/* Order Edit Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Manage Order #{selectedOrder?.id?.slice(0, 8)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Status</label>
              <Select value={updateData.status} onValueChange={(v) => setUpdateData({...updateData, status: v})}>
                <SelectTrigger data-testid="admin-order-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {orderStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Tracking Number</label>
              <Input
                value={updateData.tracking_number}
                onChange={(e) => setUpdateData({...updateData, tracking_number: e.target.value})}
                placeholder="Enter tracking number..."
                data-testid="admin-order-tracking-input"
              />
            </div>
            {selectedOrder && (
              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1">
                <p><span className="text-gray-500">Customer:</span> {selectedOrder.user_id}</p>
                <p><span className="text-gray-500">Payment:</span> {selectedOrder.payment_method} ({selectedOrder.payment_status})</p>
                <p><span className="text-gray-500">Items:</span> {selectedOrder.items?.length || 0}</p>
                <p><span className="text-gray-500">Address:</span> {selectedOrder.shipping_address?.street}, {selectedOrder.shipping_address?.city}</p>
              </div>
            )}

            {/* Tracking Stage Update */}
            {selectedOrder && (
              <div className="border-t border-gray-200 pt-4">
                <label className="text-sm font-medium text-navy block mb-3">Quick Tracking Update</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {trackingStages.map(stage => (
                    <Button
                      key={stage.key}
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={async () => {
                        try {
                          await api.post(`/api/admin/orders/${selectedOrder.id}/tracking`, {
                            stage: stage.key,
                            note: trackingNote || null,
                          });
                          toast.success(`Tracking: ${stage.label}`);
                          setTrackingNote('');
                          fetchOrders();
                        } catch {
                          toast.error('Failed to update tracking');
                        }
                      }}
                      data-testid={`tracking-${stage.key}`}
                    >
                      {stage.label}
                    </Button>
                  ))}
                </div>
                <Input
                  value={trackingNote}
                  onChange={(e) => setTrackingNote(e.target.value)}
                  placeholder="Optional note (e.g., 'Driver en route')..."
                  className="text-sm"
                  data-testid="tracking-note-input"
                />
              </div>
            )}

            {/* Assign to Partner */}
            {selectedOrder && approvedPartners.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <label className="text-sm font-medium text-navy block mb-3">Assign to Production Partner</label>
                <div className="space-y-2">
                  <Select value={assignPartner} onValueChange={setAssignPartner}>
                    <SelectTrigger data-testid="order-assign-partner-select">
                      <SelectValue placeholder="Select partner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {approvedPartners.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.company_name} — {p.city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={assignJobTitle}
                    onChange={(e) => setAssignJobTitle(e.target.value)}
                    placeholder="Job title (e.g. Business Cards Print Run)"
                    className="text-sm"
                    data-testid="order-assign-job-title"
                  />
                  <Button
                    size="sm"
                    className="bg-mint hover:bg-mint-dark text-black w-full"
                    onClick={handleAssignToPartner}
                    disabled={assigning}
                    data-testid="order-assign-partner-btn"
                  >
                    <Briefcase className="h-3.5 w-3.5 mr-1.5" />
                    {assigning ? 'Assigning...' : 'Assign Job to Partner'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedOrder(null)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleUpdateOrder} data-testid="admin-order-save-btn">
              Save & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ PRODUCTS TAB ============
function ProductsTab({ api }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'bulk'
  const [bulkPrices, setBulkPrices] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [newProduct, setNewProduct] = useState({
    name: '', description: '', category: '', base_price: '', image_url: '',
    sizes: [], paper_types: [], quantities: [], is_active: true,
  });

  const fetchProducts = async () => {
    try {
      const { data } = await api.get('/api/products');
      setProducts(data);
    } catch {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleCreate = async () => {
    try {
      await api.post('/api/admin/products', {
        ...newProduct,
        base_price: parseFloat(newProduct.base_price),
        gallery: [],
      });
      toast.success('Product created!');
      setShowCreate(false);
      setNewProduct({ name: '', description: '', category: '', base_price: '', image_url: '', sizes: [], paper_types: [], quantities: [], is_active: true });
      fetchProducts();
    } catch {
      toast.error('Failed to create product');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/products/${id}`);
      toast.success('Product deactivated');
      fetchProducts();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/admin/upload-product-image`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const imageUrl = `${API}${data.url}`;
      setEditProduct(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded!');
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleEditSave = async () => {
    if (!editProduct) return;
    try {
      const body = {
        name: editProduct.name,
        description: editProduct.description,
        category: editProduct.category,
        base_price: parseFloat(editProduct.base_price),
        image_url: editProduct.image_url,
      };
      await api.put(`/api/admin/products/${editProduct.id}`, body);
      toast.success('Product updated!');
      setEditProduct(null);
      fetchProducts();
    } catch {
      toast.error('Failed to update product');
    }
  };

  const handleBulkSave = async () => {
    const updates = Object.entries(bulkPrices)
      .filter(([, price]) => price !== '' && price !== undefined)
      .map(([id, price]) => ({ id, base_price: parseFloat(price) }));
    if (updates.length === 0) { toast.info('No changes to save'); return; }
    setBulkSaving(true);
    try {
      await api.put('/api/admin/products-bulk', { updates });
      toast.success(`Updated ${updates.length} product prices!`);
      setBulkPrices({});
      fetchProducts();
    } catch {
      toast.error('Failed to save bulk changes');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <p className="text-gray-500">{products.length} active products</p>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white text-navy shadow-sm' : 'text-gray-500'}`}
              data-testid="product-view-grid"
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('bulk')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'bulk' ? 'bg-white text-navy shadow-sm' : 'text-gray-500'}`}
              data-testid="product-view-bulk"
            >
              Bulk Edit
            </button>
          </div>
        </div>
        <Button className="bg-mint hover:bg-mint-dark text-black" onClick={() => setShowCreate(true)} data-testid="admin-add-product-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Product
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : viewMode === 'bulk' ? (
        /* Bulk Price Editor */
        <div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Product</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Category</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Current Price</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600 w-36">New Price ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-3">
                          <img src={product.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                          <span className="font-medium text-navy truncate max-w-[200px]">{product.name}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-gray-500 text-xs">{product.category}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-navy">${product.base_price?.toFixed(2)}</td>
                      <td className="py-2.5 px-4 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={product.base_price?.toFixed(2)}
                          value={bulkPrices[product.id] ?? ''}
                          onChange={(e) => setBulkPrices({ ...bulkPrices, [product.id]: e.target.value })}
                          className="w-28 ml-auto text-right h-8 text-sm"
                          data-testid={`bulk-price-${product.id?.slice(0,8)}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button
              className="bg-mint hover:bg-mint-dark text-black"
              onClick={handleBulkSave}
              disabled={bulkSaving}
              data-testid="bulk-save-btn"
            >
              <Save className="h-4 w-4 mr-2" /> {bulkSaving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => (
            <div key={product.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-navy">{product.name}</h3>
                    <p className="text-xs text-gray-500">{product.category}</p>
                  </div>
                  <span className="font-bold text-navy">${product.base_price?.toFixed(2)}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setEditProduct({ ...product })}
                    data-testid={`admin-product-edit-${product.id?.slice(0,8)}`}
                  >
                    <Edit className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-red-500 hover:bg-red-50" onClick={() => handleDelete(product.id)}>
                    <Trash2 className="h-3 w-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Product Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-navy block mb-1">Name *</label>
              <Input value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} data-testid="admin-product-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-1">Description *</label>
              <Textarea value={newProduct.description} onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-navy block mb-1">Category *</label>
                <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['Advertising', 'Stationery & Cards', 'Packaging', 'Labels & Stickers', 'Office & Documents', 'Promotional Items', 'Signage & Displays', 'Apparel & Textiles', 'Outdoor Displays', 'Specialty Items'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-1">Base Price ($) *</label>
                <Input type="number" step="0.01" value={newProduct.base_price} onChange={(e) => setNewProduct({...newProduct, base_price: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-1">Image URL *</label>
              <Input value={newProduct.image_url} onChange={(e) => setNewProduct({...newProduct, image_url: e.target.value})} placeholder="https://..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleCreate} data-testid="admin-product-save-btn">
              Create Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={!!editProduct} onOpenChange={() => setEditProduct(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Product</DialogTitle>
            <DialogDescription>Modify product details, pricing and image</DialogDescription>
          </DialogHeader>
          {editProduct && (
            <div className="space-y-4 py-4">
              {/* Image Preview + Upload */}
              <div>
                <label className="text-sm font-medium text-navy block mb-1">Product Image</label>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-xl overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                    <img src={editProduct.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={editProduct.image_url}
                      onChange={(e) => setEditProduct({...editProduct, image_url: e.target.value})}
                      placeholder="Image URL..."
                      className="text-sm"
                    />
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingImage}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="admin-edit-upload-image"
                      >
                        <Upload className="h-3 w-3 mr-1" /> {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-1">Name</label>
                <Input
                  value={editProduct.name}
                  onChange={(e) => setEditProduct({...editProduct, name: e.target.value})}
                  data-testid="admin-edit-product-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-1">Description</label>
                <Textarea
                  value={editProduct.description}
                  onChange={(e) => setEditProduct({...editProduct, description: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-navy block mb-1">Category</label>
                  <Select
                    value={editProduct.category}
                    onValueChange={(v) => setEditProduct({...editProduct, category: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Advertising', 'Stationery & Cards', 'Packaging', 'Labels & Stickers', 'Office & Documents', 'Promotional Items', 'Signage & Displays', 'Apparel & Textiles', 'Outdoor Displays', 'Specialty Items'].map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-navy block mb-1">Base Price ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editProduct.base_price}
                    onChange={(e) => setEditProduct({...editProduct, base_price: e.target.value})}
                    data-testid="admin-edit-product-price"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProduct(null)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleEditSave} data-testid="admin-edit-product-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ QUOTES TAB ============
function QuotesTab({ api }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', quoted_price: '' });

  const fetchQuotes = async () => {
    try {
      const { data } = await api.get('/api/admin/quotes');
      setQuotes(data);
    } catch {
      toast.error('Failed to fetch quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, []);

  const handleUpdate = async () => {
    try {
      const body = {};
      if (updateData.status) body.status = updateData.status;
      if (updateData.quoted_price) body.quoted_price = parseFloat(updateData.quoted_price);
      await api.put(`/api/admin/quotes/${selectedQuote.id}`, body);
      toast.success('Quote updated!');
      setSelectedQuote(null);
      fetchQuotes();
    } catch {
      toast.error('Failed to update quote');
    }
  };

  return (
    <div>
      <ContentState loading={loading} empty={quotes.length === 0} emptyContent={<div className="text-center py-12 text-gray-500">No quote requests yet</div>}>
        <div className="space-y-3">
          {quotes.map(quote => (
            <div key={quote.id} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-medium text-navy">{quote.product_category}</span>
                    <Badge className={statusColors[quote.status] || 'bg-gray-100'}>{quote.status}</Badge>
                    {quote.quoted_price && <Badge variant="outline">${quote.quoted_price}</Badge>}
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-1">{quote.description}</p>
                  <p className="text-xs text-gray-400 mt-1">{quote.contact_email} - Qty: {quote.quantity} - {new Date(quote.created_at).toLocaleDateString()}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => { setSelectedQuote(quote); setUpdateData({ status: quote.status, quoted_price: quote.quoted_price || '' }); }}>
                  <Edit className="h-4 w-4 mr-1" /> Respond
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ContentState>

      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Respond to Quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2">
              <p><span className="font-medium">Category:</span> {selectedQuote?.product_category}</p>
              <p><span className="font-medium">Description:</span> {selectedQuote?.description}</p>
              <p><span className="font-medium">Quantity:</span> {selectedQuote?.quantity}</p>
              <p><span className="font-medium">Contact:</span> {selectedQuote?.contact_email}</p>
              {selectedQuote?.deadline && <p><span className="font-medium">Deadline:</span> {selectedQuote.deadline}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Status</label>
              <Select value={updateData.status} onValueChange={(v) => setUpdateData({...updateData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {quoteStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Quoted Price ($)</label>
              <Input type="number" step="0.01" value={updateData.quoted_price} onChange={(e) => setUpdateData({...updateData, quoted_price: e.target.value})} placeholder="Enter price..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedQuote(null)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleUpdate}>Save Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ PROJECTS TAB ============
function ProjectsTab({ api }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', admin_notes: '', quoted_price: '' });

  const fetchProjects = async () => {
    try {
      const { data } = await api.get('/api/admin/custom-projects');
      setProjects(data);
    } catch {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleUpdate = async () => {
    try {
      const body = {};
      if (updateData.status) body.status = updateData.status;
      if (updateData.admin_notes) body.admin_notes = updateData.admin_notes;
      if (updateData.quoted_price) body.quoted_price = parseFloat(updateData.quoted_price);
      await api.put(`/api/admin/custom-projects/${selectedProject.id}`, body);
      toast.success('Project updated!');
      setSelectedProject(null);
      fetchProjects();
    } catch {
      toast.error('Failed to update project');
    }
  };

  return (
    <div>
      <ContentState loading={loading} empty={projects.length === 0} emptyContent={<div className="text-center py-12 text-gray-500">No custom projects yet</div>}>
        <div className="space-y-3">
          {projects.map(project => (
            <div key={project.id} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-medium text-navy">{project.title}</span>
                    <Badge className={statusColors[project.status] || 'bg-gray-100'}>{project.status}</Badge>
                    <Badge variant="outline">{project.project_type}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-1">{project.description}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Budget: {project.budget_range || 'N/A'} - {new Date(project.created_at).toLocaleDateString()}
                    {project.inspiration_images?.length > 0 && ` - ${project.inspiration_images.length} images`}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => { 
                  setSelectedProject(project); 
                  setUpdateData({ status: project.status, admin_notes: project.admin_notes || '', quoted_price: project.quoted_price || '' }); 
                }}>
                  <Edit className="h-4 w-4 mr-1" /> Manage
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ContentState>

      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Manage Project: {selectedProject?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2">
              <p><span className="font-medium">Type:</span> {selectedProject?.project_type}</p>
              <p><span className="font-medium">Budget:</span> {selectedProject?.budget_range || 'N/A'}</p>
              <p><span className="font-medium">Description:</span> {selectedProject?.description}</p>
              {selectedProject?.deadline && <p><span className="font-medium">Deadline:</span> {selectedProject.deadline}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Status</label>
              <Select value={updateData.status} onValueChange={(v) => setUpdateData({...updateData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {projectStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Quoted Price ($)</label>
              <Input type="number" step="0.01" value={updateData.quoted_price} onChange={(e) => setUpdateData({...updateData, quoted_price: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Admin Notes</label>
              <Textarea value={updateData.admin_notes} onChange={(e) => setUpdateData({...updateData, admin_notes: e.target.value})} rows={3} placeholder="Internal notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedProject(null)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ ANALYTICS TAB ============
function AnalyticsTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const { data: analytics } = await api.get('/api/admin/analytics');
        setData(analytics);
      } catch {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [api]);

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No analytics data</div>;

  const handleExportCSV = () => {
    window.open(`${API}/api/admin/analytics/export`, '_blank');
  };

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      {/* Export Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-2" /> Export Revenue CSV
        </Button>
      </div>
      {/* Revenue Chart */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="h-5 w-5 text-mint" />
          <h3 className="font-heading font-semibold text-navy text-lg">Weekly Revenue</h3>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.weekly_revenue}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="week" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
            <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']} />
            <Bar dataKey="revenue" fill="#6BCDB0" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Order Status Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200">
          <h3 className="font-heading font-semibold text-navy text-lg mb-6">Order Status Breakdown</h3>
          {data.status_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={data.status_breakdown}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ status, count }) => `${status} (${count})`}
                >
                  {data.status_breakdown.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-center py-8">No orders yet</p>
          )}
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200">
          <h3 className="font-heading font-semibold text-navy text-lg mb-6">Top Selling Products</h3>
          {data.top_products.length > 0 ? (
            <div className="space-y-3">
              {data.top_products.map((product, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-mint/10 text-mint text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="text-sm font-medium text-navy truncate max-w-[180px]">{product.name || 'Unknown'}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-navy">{product.count} sold</span>
                    <p className="text-xs text-gray-400">${product.revenue?.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No sales data yet</p>
          )}
        </div>
      </div>

      {/* Monthly Revenue Trend */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <h3 className="font-heading font-semibold text-navy text-lg mb-6">Monthly Revenue Trend</h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.monthly_revenue}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
            <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']} />
            <Line type="monotone" dataKey="revenue" stroke="#2D3E60" strokeWidth={2.5} dot={{ fill: '#6BCDB0', r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============ PARTNER APPLICATIONS TAB ============
const partnerStatuses = ['pending', 'reviewed', 'approved', 'rejected'];

function PartnersTab({ api }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', admin_notes: '' });
  const [showAssignJob, setShowAssignJob] = useState(null);
  const [jobForm, setJobForm] = useState({ title: '', description: '', deadline: '' });
  const [showChat, setShowChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');

  const fetchApps = async () => {
    try {
      const { data } = await api.get('/api/admin/partner-applications');
      setApplications(data);
    } catch {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApps(); }, []);

  const handleUpdate = async () => {
    try {
      const body = {};
      if (updateData.status) body.status = updateData.status;
      if (updateData.admin_notes) body.admin_notes = updateData.admin_notes;
      await api.put(`/api/admin/partner-applications/${selectedApp.id}`, body);
      toast.success('Application updated!');
      setSelectedApp(null);
      fetchApps();
    } catch {
      toast.error('Failed to update application');
    }
  };

  const handleAssignJob = async () => {
    if (!jobForm.title) { toast.error('Job title is required'); return; }
    try {
      await api.post('/api/admin/partner-jobs', {
        partner_id: showAssignJob.id,
        title: jobForm.title,
        description: jobForm.description,
        deadline: jobForm.deadline || null,
      });
      toast.success(`Job assigned to ${showAssignJob.company_name}!`);
      setShowAssignJob(null);
      setJobForm({ title: '', description: '', deadline: '' });
    } catch {
      toast.error('Failed to assign job');
    }
  };

  const openChat = async (app) => {
    setShowChat(app);
    try {
      const { data } = await api.get(`/api/admin/partner-messages/${app.id}`);
      setChatMessages(data);
    } catch {
      setChatMessages([]);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    try {
      await api.post(`/api/admin/partner-messages/${showChat.id}`, { message: chatInput });
      setChatInput('');
      const { data } = await api.get(`/api/admin/partner-messages/${showChat.id}`);
      setChatMessages(data);
    } catch {
      toast.error('Failed to send message');
    }
  };

  const partnerStatusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    reviewed: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  return (
    <div>
      <ContentState loading={loading} empty={applications.length === 0} emptyContent={<div className="text-center py-12 text-gray-500">No partner applications yet</div>}>
        <div className="space-y-3">
          {applications.map(app => (
            <div key={app.id} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-medium text-navy">{app.company_name}</span>
                    <Badge className={partnerStatusColors[app.status] || 'bg-gray-100'}>{app.status}</Badge>
                    <Badge variant="outline">{app.city}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {app.contact_name} - {app.email} - {app.phone}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Services: {app.services?.join(', ') || 'N/A'} — {new Date(app.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {app.status === 'approved' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-mint border-mint/30 hover:bg-mint/10"
                        onClick={() => setShowAssignJob(app)}
                        data-testid={`admin-partner-assign-${app.id?.slice(0,8)}`}
                      >
                        <Briefcase className="h-3.5 w-3.5 mr-1" /> Assign Job
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openChat(app)}
                        data-testid={`admin-partner-chat-${app.id?.slice(0,8)}`}
                      >
                        <MessageSquare className="h-3.5 w-3.5 mr-1" /> Chat
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSelectedApp(app); setUpdateData({ status: app.status, admin_notes: app.admin_notes || '' }); }}
                    data-testid={`admin-partner-manage-${app.id?.slice(0,8)}`}
                  >
                    <Edit className="h-3.5 w-3.5 mr-1" /> Manage
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ContentState>

      {/* Manage Application Dialog */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Partner Application: {selectedApp?.company_name}</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2">
                <p><span className="font-medium">Contact:</span> {selectedApp.contact_name}</p>
                <p><span className="font-medium">Email:</span> {selectedApp.email}</p>
                <p><span className="font-medium">Phone:</span> {selectedApp.phone}</p>
                <p><span className="font-medium">City:</span> {selectedApp.city}</p>
                <p><span className="font-medium">Services:</span> {selectedApp.services?.join(', ')}</p>
                <p><span className="font-medium">Equipment:</span> {selectedApp.equipment || 'N/A'}</p>
                <p><span className="font-medium">Experience:</span> {selectedApp.experience_years || 'N/A'} years</p>
                <p><span className="font-medium">Website:</span> {selectedApp.website || 'N/A'}</p>
                {selectedApp.message && <p><span className="font-medium">Message:</span> {selectedApp.message}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Status</label>
                <Select value={updateData.status} onValueChange={(v) => setUpdateData({...updateData, status: v})}>
                  <SelectTrigger data-testid="admin-partner-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {partnerStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Admin Notes</label>
                <Textarea
                  value={updateData.admin_notes}
                  onChange={(e) => setUpdateData({...updateData, admin_notes: e.target.value})}
                  rows={3}
                  placeholder="Internal notes..."
                  data-testid="admin-partner-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedApp(null)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleUpdate} data-testid="admin-partner-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Job Dialog */}
      <Dialog open={!!showAssignJob} onOpenChange={() => setShowAssignJob(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign Job to {showAssignJob?.company_name}</DialogTitle>
            <DialogDescription>Create a production job for this partner</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-navy block mb-1.5">Job Title *</label>
              <Input
                value={jobForm.title}
                onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                placeholder="e.g. Business Cards - Order #A1B2C3"
                data-testid="assign-job-title"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-1.5">Description</label>
              <Textarea
                value={jobForm.description}
                onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                rows={3}
                placeholder="Job details, specs, quantity..."
                data-testid="assign-job-description"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-1.5">Deadline</label>
              <Input
                type="date"
                value={jobForm.deadline}
                onChange={(e) => setJobForm({...jobForm, deadline: e.target.value})}
                data-testid="assign-job-deadline"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignJob(null)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleAssignJob} data-testid="assign-job-submit">
              Assign Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Chat Dialog */}
      <Dialog open={!!showChat} onOpenChange={() => setShowChat(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Chat with {showChat?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 py-4 min-h-[200px] max-h-[400px]" data-testid="admin-partner-chat-messages">
            {chatMessages.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No messages yet. Start the conversation!</p>
            ) : chatMessages.map(msg => (
              <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                  msg.sender === 'admin'
                    ? 'bg-mint/10 text-navy'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <p className="text-xs font-medium mb-1 opacity-60">{msg.sender === 'admin' ? 'You' : msg.partner_name}</p>
                  <p>{msg.message}</p>
                  <p className="text-[10px] opacity-40 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Type a message..."
              onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
              data-testid="admin-partner-chat-input"
            />
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={sendChatMessage} data-testid="admin-partner-chat-send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ EMAIL SETTINGS TAB ============
function EmailSettingsTab({ api }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get('/api/admin/email-settings');
        setSettings(data);
      } catch {
        toast.error('Failed to load email settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/email-settings', settings);
      toast.success('Email settings saved!');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  if (!settings) return null;

  const previewColor = settings.primary_color || '#6BCDB0';
  const previewName = settings.company_name || 'Printed LB';
  const previewFooter = (settings.footer_text || '').replace(/\n/g, '<br/>');
  const previewEmail = settings.support_email || 'info@printedlb.com';

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="email-settings-tab">
      {/* Settings Form */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-5 w-5 text-mint" />
          <h3 className="font-heading font-semibold text-navy text-lg">Email Template Settings</h3>
        </div>
        <p className="text-sm text-gray-500">Customize the branding and content used in all automated emails.</p>

        <div>
          <label className="text-sm font-medium text-navy block mb-1.5">Logo URL</label>
          <Input
            value={settings.logo_url || ''}
            onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
            placeholder="https://..."
            data-testid="email-settings-logo"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-navy block mb-1.5">Company Name</label>
            <Input
              value={settings.company_name || ''}
              onChange={(e) => setSettings({...settings, company_name: e.target.value})}
              data-testid="email-settings-company"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-navy block mb-1.5">Primary Color</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={settings.primary_color || '#6BCDB0'}
                onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
              />
              <Input
                value={settings.primary_color || ''}
                onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                placeholder="#6BCDB0"
                data-testid="email-settings-color"
              />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-navy block mb-1.5">Support Email</label>
            <Input
              value={settings.support_email || ''}
              onChange={(e) => setSettings({...settings, support_email: e.target.value})}
              data-testid="email-settings-support-email"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-navy block mb-1.5">Support Phone</label>
            <Input
              value={settings.support_phone || ''}
              onChange={(e) => setSettings({...settings, support_phone: e.target.value})}
              data-testid="email-settings-support-phone"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-navy block mb-1.5">Footer Text</label>
          <Textarea
            value={settings.footer_text || ''}
            onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
            rows={3}
            placeholder="Footer text for all emails..."
            data-testid="email-settings-footer"
          />
        </div>

        <Button
          className="bg-mint hover:bg-mint-dark text-black"
          onClick={handleSave}
          disabled={saving}
          data-testid="email-settings-save"
        >
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Email Settings'}
        </Button>
      </div>

      {/* Live Email Preview */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="h-5 w-5 text-mint" />
          <h3 className="font-heading font-semibold text-navy text-lg">Live Preview</h3>
        </div>
        <div className="bg-gray-100 rounded-xl p-4 overflow-hidden" data-testid="email-preview">
          <div style={{ fontFamily: 'Manrope, Arial, sans-serif', maxWidth: 400, margin: '0 auto', background: '#fafafa', padding: 20 }}>
            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {settings.logo_url && (
                <img src={settings.logo_url} alt={previewName} style={{ height: 40 }} />
              )}
            </div>
            {/* Card */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, background: `${previewColor}20`, borderRadius: '50%', margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18, color: previewColor }}>&#10003;</span>
                </div>
                <h2 style={{ color: '#2D3E60', fontSize: 16, margin: 0 }}>Order Confirmed!</h2>
                <p style={{ color: '#4B5563', fontSize: 11, margin: '4px 0 0' }}>Thank you for your order</p>
              </div>
              <div style={{ background: '#f3f4f6', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280' }}>Order Number</span>
                  <span style={{ fontWeight: 700, color: '#2D3E60' }}>#A1B2C3D4</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                  <span style={{ color: '#6B7280' }}>Date</span>
                  <span style={{ color: '#2D3E60' }}>2026-02-27</span>
                </div>
              </div>
              <h3 style={{ color: '#2D3E60', fontSize: 12, margin: '0 0 8px', borderBottom: `2px solid ${previewColor}`, paddingBottom: 4 }}>Receipt</h3>
              <div style={{ fontSize: 11, borderBottom: '1px solid #eee', padding: '6px 0', display: 'flex', justifyContent: 'space-between' }}>
                <span>Business Cards x100</span>
                <span style={{ fontWeight: 600 }}>$25.00</span>
              </div>
              <div style={{ fontSize: 11, borderBottom: '1px solid #eee', padding: '6px 0', display: 'flex', justifyContent: 'space-between' }}>
                <span>Flyers A4 x500</span>
                <span style={{ fontWeight: 600 }}>$45.00</span>
              </div>
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `2px solid #2D3E60`, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 800, color: '#2D3E60', fontSize: 14 }}>Total</span>
                <span style={{ fontWeight: 800, color: '#2D3E60', fontSize: 14 }}>$70.00</span>
              </div>
            </div>
            {/* Footer */}
            <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 10, marginTop: 16, lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: previewFooter || `${previewName}<br/>${previewEmail}` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ REVIEWS TAB ============
function ReviewsTab({ api }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = async () => {
    try {
      const { data } = await api.get('/api/admin/reviews');
      setReviews(data);
    } catch {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  const toggleApproval = async (review) => {
    try {
      await api.put(`/api/admin/reviews/${review.id}`, { is_approved: !review.is_approved });
      toast.success(review.is_approved ? 'Review hidden' : 'Review approved!');
      fetchReviews();
    } catch {
      toast.error('Failed to update review');
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/api/admin/reviews/${id}`);
      toast.success('Review deleted');
      fetchReviews();
    } catch {
      toast.error('Failed to delete review');
    }
  };

  return (
    <div>
      <p className="text-gray-500 mb-6">{reviews.length} review(s) — {reviews.filter(r => r.is_approved).length} approved</p>
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No reviews yet</div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <div key={review.id} className={`bg-white rounded-xl p-4 border ${review.is_approved ? 'border-green-200' : 'border-gray-200'}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`h-3.5 w-3.5 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                      ))}
                    </div>
                    <Badge className={review.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {review.is_approved ? 'Approved' : 'Pending'}
                    </Badge>
                  </div>
                  <h4 className="font-medium text-navy">{review.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{review.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{review.user_name} — {new Date(review.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className={review.is_approved ? 'text-yellow-600' : 'text-green-600'}
                    onClick={() => toggleApproval(review)}
                    data-testid={`review-toggle-${review.id?.slice(0,8)}`}
                  >
                    {review.is_approved ? 'Hide' : 'Approve'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => deleteReview(review.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ COUPONS TAB ============
function CouponsTab({ api }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_uses: '', expires_at: '',
  });

  const fetchCoupons = async () => {
    try {
      const { data } = await api.get('/api/admin/coupons');
      setCoupons(data);
    } catch {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleCreate = async () => {
    if (!newCoupon.code || !newCoupon.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }
    try {
      await api.post('/api/admin/coupons', {
        code: newCoupon.code,
        discount_type: newCoupon.discount_type,
        discount_value: parseFloat(newCoupon.discount_value),
        min_order: newCoupon.min_order ? parseFloat(newCoupon.min_order) : 0,
        max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
        expires_at: newCoupon.expires_at || null,
      });
      toast.success('Coupon created!');
      setShowCreate(false);
      setNewCoupon({ code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_uses: '', expires_at: '' });
      fetchCoupons();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const toggleCoupon = async (coupon) => {
    try {
      await api.put(`/api/admin/coupons/${coupon.id}`, { is_active: !coupon.is_active });
      toast.success(coupon.is_active ? 'Coupon deactivated' : 'Coupon activated');
      fetchCoupons();
    } catch {
      toast.error('Failed to update coupon');
    }
  };

  const deleteCoupon = async (id) => {
    try {
      await api.delete(`/api/admin/coupons/${id}`);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch {
      toast.error('Failed to delete coupon');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-500">{coupons.length} coupon(s)</p>
        <Button className="bg-mint hover:bg-mint-dark text-black" onClick={() => setShowCreate(true)} data-testid="admin-add-coupon-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Coupon
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No coupons yet. Create your first coupon code!</div>
      ) : (
        <div className="space-y-3">
          {coupons.map(coupon => (
            <div key={coupon.id} className={`bg-white rounded-xl p-4 border ${coupon.is_active ? 'border-gray-200' : 'border-gray-200 opacity-50'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge className="bg-navy/10 text-navy font-mono text-sm">{coupon.code}</Badge>
                    <Badge className={coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <span className="text-sm font-semibold text-navy">
                      {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% off` : `$${coupon.discount_value} off`}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                    {coupon.min_order > 0 && <span>Min order: ${coupon.min_order}</span>}
                    {coupon.max_uses && <span>Uses: {coupon.used_count || 0}/{coupon.max_uses}</span>}
                    {!coupon.max_uses && <span>Uses: {coupon.used_count || 0}/unlimited</span>}
                    {coupon.expires_at && <span>Expires: {coupon.expires_at.slice(0, 10)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleCoupon(coupon)} data-testid={`coupon-toggle-${coupon.code}`}>
                    {coupon.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => deleteCoupon(coupon.id)} data-testid={`coupon-delete-${coupon.code}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Coupon Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Coupon</DialogTitle>
            <DialogDescription>Set up a new discount code for customers</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-navy block mb-1.5">Code *</label>
              <Input
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                placeholder="e.g. SAVE20"
                className="font-mono"
                data-testid="coupon-create-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-navy block mb-1.5">Type</label>
                <Select value={newCoupon.discount_type} onValueChange={(v) => setNewCoupon({...newCoupon, discount_type: v})}>
                  <SelectTrigger data-testid="coupon-create-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-1.5">Discount Value *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={newCoupon.discount_value}
                  onChange={(e) => setNewCoupon({...newCoupon, discount_value: e.target.value})}
                  placeholder={newCoupon.discount_type === 'percentage' ? 'e.g. 20' : 'e.g. 10.00'}
                  data-testid="coupon-create-value"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-navy block mb-1.5">Min Order ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={newCoupon.min_order}
                  onChange={(e) => setNewCoupon({...newCoupon, min_order: e.target.value})}
                  placeholder="0"
                  data-testid="coupon-create-min"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-1.5">Max Uses</label>
                <Input
                  type="number"
                  value={newCoupon.max_uses}
                  onChange={(e) => setNewCoupon({...newCoupon, max_uses: e.target.value})}
                  placeholder="Unlimited"
                  data-testid="coupon-create-max-uses"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-1.5">Expiry Date</label>
              <Input
                type="datetime-local"
                value={newCoupon.expires_at}
                onChange={(e) => setNewCoupon({...newCoupon, expires_at: e.target.value})}
                data-testid="coupon-create-expires"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleCreate} data-testid="coupon-create-submit">
              Create Coupon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ MAIN ADMIN PAGE ============
export function AdminPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }

    const fetchStats = async () => {
      try {
        const { data } = await api.get('/api/admin/stats');
        setStats(data);
      } catch {
        toast.error('Failed to load admin stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, api, navigate]);

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-panel">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-navy">Admin Panel</h1>
            <p className="text-gray-500 mt-1">Manage your printing business</p>
          </div>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-mint" />
            <span className="text-sm text-gray-500">Dashboard</span>
          </div>
        </div>

        {/* Stats */}
        {loading ? (
          <div className="flex justify-center py-8"><div className="spinner"></div></div>
        ) : stats && (
          <StatsCards stats={stats} />
        )}

        {/* Tabs */}
        <Tabs defaultValue="orders" className="mt-4">
          <TabsList className="bg-white border border-gray-200 rounded-xl p-1 mb-6 flex-wrap">
            <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-orders">
              <Package className="h-4 w-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="products" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-products">
              <ShoppingBag className="h-4 w-4 mr-2" /> Products
            </TabsTrigger>
            <TabsTrigger value="quotes" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-quotes">
              <FileText className="h-4 w-4 mr-2" /> Quotes
            </TabsTrigger>
            <TabsTrigger value="projects" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-projects">
              <Palette className="h-4 w-4 mr-2" /> Projects
            </TabsTrigger>
            <TabsTrigger value="partners" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-partners">
              <Building2 className="h-4 w-4 mr-2" /> Partners
            </TabsTrigger>
            <TabsTrigger value="coupons" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-coupons">
              <Tag className="h-4 w-4 mr-2" /> Coupons
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-reviews">
              <Star className="h-4 w-4 mr-2" /> Reviews
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-analytics">
              <TrendingUp className="h-4 w-4 mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-mint/10 data-[state=active]:text-navy" data-testid="admin-tab-settings">
              <Settings className="h-4 w-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders"><OrdersTab api={api} /></TabsContent>
          <TabsContent value="products"><ProductsTab api={api} /></TabsContent>
          <TabsContent value="quotes"><QuotesTab api={api} /></TabsContent>
          <TabsContent value="projects"><ProjectsTab api={api} /></TabsContent>
          <TabsContent value="partners"><PartnersTab api={api} /></TabsContent>
          <TabsContent value="coupons"><CouponsTab api={api} /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab api={api} /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab api={api} /></TabsContent>
          <TabsContent value="settings"><EmailSettingsTab api={api} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
